/**
 * Что автор темы написал на ней сам: надписи, списки дел, привычки.
 *
 * Зачем: страница темы — это картинка, заголовок и общий для всех тем
 * шаблон. На 08.10.2026 из выборки тем в карте сайта Google не взял в индекс
 * больше половины («Crawled / Discovered — currently not indexed»), а показы
 * получают темы с узкими названиями вроде «JLPT Countdown». Надписи вроде
 * «JLPT N2» или «Final exams» — единственный текст, который у каждой темы
 * свой и при этом ровно то, что человек увидит после установки.
 *
 * Заметки (NoteModel) не берём: туда пишут личное, а на странице темы это
 * превратилось бы в проиндексированный текст.
 *
 * @see docs/seo/research/google/README.md
 */
import { collectWidgets } from './themeMadeWith';

/** Подсказка пустого воркспейса из расширения — не текст автора */
const PLACEHOLDERS = ['you can add widgets to this workspace'];

const MAX_TEXT = 120;
const MAX_TEXTS = 12;
const MAX_LISTS = 6;
const MAX_TASKS = 8;
const MAX_HABITS = 10;

function clean(value) {
	if (typeof value !== 'string') return '';

	const text = value.replace(/\s+/g, ' ').trim();
	if (text.length < 2) return '';
	if (PLACEHOLDERS.includes(text.toLowerCase())) return '';

	return text.length > MAX_TEXT
		? `${text.slice(0, MAX_TEXT - 1).trim()}…`
		: text;
}

/** Без повторов: одна и та же надпись часто стоит на нескольких воркспейсах */
function unique(values, limit) {
	const seen = new Set();
	const out = [];

	values.forEach(value => {
		const key = value.toLowerCase();
		if (!value || seen.has(key) || out.length >= limit) return;
		seen.add(key);
		out.push(value);
	});

	return out;
}

/**
 * @param appJSON разобранный appJSON темы
 * @returns {{texts: string[], lists: {title: string, tasks: string[]}[], habits: string[]}}
 */
export default function getThemeTexts(appJSON) {
	// В 4.0 виджеты бывают вложены в контейнеры
	const widgets = collectWidgets(appJSON && appJSON.widgets);

	const byClass = name =>
		widgets.filter(widget => widget && widget.className === name);

	const texts = unique(
		byClass('TextModel').map(widget => clean(widget.text)),
		MAX_TEXTS,
	);

	const seenLists = new Set();
	const lists = [];
	byClass('TodoModel').forEach(widget => {
		const tasks = unique(
			(Array.isArray(widget.tasks) ? widget.tasks : []).map(task =>
				clean(task && task.title),
			),
			MAX_TASKS,
		);
		if (tasks.length === 0) return;

		const title = clean(widget.title);
		const key = `${title}\n${tasks.join('\n')}`.toLowerCase();
		if (seenLists.has(key) || lists.length >= MAX_LISTS) return;
		seenLists.add(key);
		lists.push({ title, tasks });
	});

	const habits = unique(
		byClass('HabitModel').map(widget => clean(widget.title)),
		MAX_HABITS,
	);

	return { texts, lists, habits };
}
